import React, { useEffect, useMemo } from 'react';
import * as d3 from 'd3';
import { useChartDimensions } from '../hooks/useChartDimensions';
import { filterVisibleItems, useLegendToggle } from '../hooks/useLegendToggle';
import ChartLegend from './ChartLegend';

/**
 * Multi-series line chart with optional area fill.
 *
 * series: [{ key, label, color, area?, dashed?, target? }]
 */
export default function LineChart({
  data,
  xKey,
  series,
  xType = 'time',
  xFormat,
  yFormat = (v) => v.toLocaleString(),
  tooltipFormat,
  heightRatio = 0.5,
  yDomain,
  pointLabel = false,
  onPointClick,
}) {
  const { containerRef, svgRef, tooltipRef, dimensions, hideTooltip } = useChartDimensions(
    (w) => Math.min(420, Math.max(260, w * heightRatio))
  );

  const seriesKeys = useMemo(() => (series || []).map((s) => s.key), [series]);
  const seriesLabels = useMemo(
    () => Object.fromEntries((series || []).map((s) => [s.key, s.label || s.key])),
    [series]
  );
  const { isVisible, toggle, statusMessage, hiddenKeys } = useLegendToggle(seriesKeys, {
    labels: seriesLabels,
  });
  const visibleSeries = useMemo(
    () => filterVisibleItems(series, hiddenKeys),
    [series, hiddenKeys]
  );

  useEffect(() => {
    if (!data?.length || !dimensions.width) {
      hideTooltip();
      return;
    }
    const { width, height } = dimensions;
    const margin = { top: 20, right: 28, bottom: 54, left: 70 };
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    svg.attr('viewBox', `0 0 ${width} ${height}`);
    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    let x;
    if (xType === 'time') {
      const parsed = data.map((d) => new Date(d[xKey] + (d[xKey].length === 7 ? '-01' : '') + 'T00:00:00'));
      x = d3.scaleTime().domain(d3.extent(parsed)).range([0, innerW]);
    } else {
      x = d3.scaleBand().domain(data.map((d) => d[xKey])).range([0, innerW]).padding(0.1);
    }

    const allVals = visibleSeries.flatMap((s) => data.map((d) => d[s.key]).filter((v) => v != null));
    if (visibleSeries.some((s) => s.target != null)) {
      allVals.push(...visibleSeries.map((s) => s.target).filter((v) => v != null));
    }
    const yMax = d3.max(allVals) || 1;
    const y = d3.scaleLinear().domain(yDomain || [0, yMax * 1.1]).range([innerH, 0]).nice();

    g.append('g').attr('class', 'grid')
      .call(d3.axisLeft(y).ticks(5).tickSize(-innerW).tickFormat(''))
      .selectAll('line').attr('stroke', '#E2E8F0').attr('stroke-dasharray', '3,3');
    g.selectAll('.grid .domain').remove();

    const getX = (d) => {
      if (xType === 'time') return x(new Date(d[xKey] + (d[xKey].length === 7 ? '-01' : '') + 'T00:00:00'));
      return x(d[xKey]) + x.bandwidth() / 2;
    };

    const defs = svg.append('defs');
    visibleSeries.forEach((s, si) => {
      const validData = data.filter((d) => d[s.key] != null);
      if (!validData.length) return;
      if (s.target != null) {
        g.append('line')
          .attr('x1', 0).attr('x2', innerW)
          .attr('y1', y(s.target)).attr('y2', y(s.target))
          .attr('stroke', s.color).attr('stroke-width', 1.5).attr('stroke-dasharray', '6,4').attr('opacity', 0.4);
        g.append('text')
          .attr('x', innerW - 4).attr('y', y(s.target) - 6)
          .attr('text-anchor', 'end').attr('font-size', '10px').attr('fill', s.color).attr('font-weight', '600')
          .text(`Target: ${yFormat(s.target)}`);
      }
      if (s.area) {
        const gradId = `area-grad-${si}`;
        const grad = defs.append('linearGradient').attr('id', gradId)
          .attr('x1', '0').attr('y1', '0').attr('x2', '0').attr('y2', '1');
        grad.append('stop').attr('offset', '0%').attr('stop-color', s.color).attr('stop-opacity', 0.18);
        grad.append('stop').attr('offset', '100%').attr('stop-color', s.color).attr('stop-opacity', 0.02);

        const area = d3.area().x((d) => getX(d)).y0(innerH).y1((d) => y(d[s.key])).curve(d3.curveMonotoneX);
        g.append('path').datum(validData).attr('fill', `url(#${gradId})`).attr('d', area);
      }
      const line = d3.line().x((d) => getX(d)).y((d) => y(d[s.key])).curve(d3.curveMonotoneX);
      g.append('path').datum(validData)
        .attr('fill', 'none').attr('stroke', s.color).attr('stroke-width', 2.5)
        .attr('stroke-dasharray', s.dashed ? '6,3' : 'none')
        .attr('d', line);

      g.selectAll(`.dot-${si}`).data(validData).enter().append('circle')
        .attr('cx', (d) => getX(d)).attr('cy', (d) => y(d[s.key]))
        .attr('r', onPointClick ? 4.5 : 3).attr('fill', s.color).attr('stroke', '#fff').attr('stroke-width', 1.5)
        .attr('cursor', onPointClick ? 'pointer' : 'default')
        .on('click', (event, d) => {
          if (!onPointClick) return;
          event.stopPropagation();
          onPointClick(d, s);
        });

      if (pointLabel && validData.length <= 16) {
        g.selectAll(`.pt-label-${si}`).data(validData).enter().append('text')
          .attr('x', (d) => getX(d))
          .attr('y', (d) => y(d[s.key]) - 8)
          .attr('text-anchor', 'middle')
          .attr('font-size', '10px')
          .attr('font-weight', '600')
          .attr('fill', s.color)
          .text((d) => yFormat(d[s.key]));
      }
    });

    const xAxis = xType === 'time'
      ? d3.axisBottom(x).ticks(Math.min(data.length, 12)).tickFormat(xFormat || d3.timeFormat('%b %y'))
      : d3.axisBottom(x).tickFormat(xFormat || ((d) => d));

    g.append('g').attr('transform', `translate(0,${innerH})`)
      .call(xAxis).selectAll('text')
      .attr('font-size', '11px').attr('fill', '#64748B')
      .attr('transform', 'rotate(-32)').attr('text-anchor', 'end');

    g.append('g').call(d3.axisLeft(y).ticks(5).tickFormat(yFormat))
      .selectAll('text').attr('font-size', '11px').attr('fill', '#64748B');

    g.selectAll('.domain').attr('stroke', '#CBD5E1');
    g.selectAll('.tick line').attr('stroke', '#CBD5E1');

    const tooltip = d3.select(tooltipRef.current);
    const showAt = (event) => {
      const [mx] = d3.pointer(event);
      let bestIdx = 0;
      let bestDist = Infinity;
      data.forEach((row, i) => {
        const dist = Math.abs(getX(row) - mx);
        if (dist < bestDist) {
          bestDist = dist;
          bestIdx = i;
        }
      });
      const d = data[bestIdx];
      const html = tooltipFormat
        ? tooltipFormat(d)
        : visibleSeries.map((s) => `<div class="tip-row"><span>${s.label}</span><span class="tip-value" style="color:${s.color}">${d[s.key] != null ? yFormat(d[s.key]) : '—'}</span></div>`).join('');
      tooltip.style('opacity', 1)
        .style('left', `${event.clientX + 12}px`).style('top', `${event.clientY - 10}px`)
        .html(`<div class="tip-label">${d[xKey]}</div>${html}`);
    };

    // Full-plot overlay so mouseleave always fires when leaving the chart area.
    g.append('rect')
      .attr('class', 'chart-pointer-overlay')
      .attr('width', innerW)
      .attr('height', innerH)
      .attr('fill', 'transparent')
      .style('cursor', onPointClick ? 'pointer' : 'crosshair')
      .on('mousemove', showAt)
      .on('mouseleave', hideTooltip)
      .on('click', (event) => {
        if (!onPointClick) return;
        const [mx] = d3.pointer(event);
        let bestIdx = 0;
        let bestDist = Infinity;
        data.forEach((row, i) => {
          const dist = Math.abs(getX(row) - mx);
          if (dist < bestDist) {
            bestDist = dist;
            bestIdx = i;
          }
        });
        onPointClick(data[bestIdx], visibleSeries[0] || null);
      });

    return hideTooltip;
  }, [data, dimensions, xKey, visibleSeries, xType, xFormat, yFormat, tooltipFormat, yDomain, pointLabel, onPointClick, hideTooltip, svgRef, tooltipRef]);

  if (!data?.length) return <div className="chart-container"><div className="no-data">No data</div></div>;
  return (
    <div className="chart-container" ref={containerRef}>
      <svg ref={svgRef} />
      <div className="d3-tooltip" ref={tooltipRef} style={{ opacity: 0 }} />
      <ChartLegend
        items={series}
        isVisible={isVisible}
        onToggle={toggle}
        statusMessage={statusMessage}
      />
    </div>
  );
}
